import * as moment from 'moment';

/**
 * Where "now" sits in a day's appointment list, and how far the day is running behind or ahead.
 * Worked out on the client from the appointments already loaded; `now` is passed in so the
 * caller decides when to tick.
 */

export type DriftLevel = 'good' | 'warn' | 'bad';

export interface ScheduleDrift {
  /** Positive is behind schedule, negative is ahead. */
  minutes: number;
  level: DriftLevel;
  text: string;
}

export interface NowLine {
  /** The row the line belongs to; equal to the row count when it goes below them all. */
  index: number;
  /** How far down that row (0 to 1) when the clock is inside the appointment; null puts it above the row. */
  within: number | null;
}

/**
 * Inside the first appointment the clock is in, part of the way down; otherwise above the first
 * timed row still to come. All-day rows stay above the line.
 */
export function nowLine(appointments: any[], now: Date): NowLine {
  const rows = appointments || [];
  const nowMinutes = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
  const inside = rows.findIndex(a => {
    if (a?.isAllDay) return false;
    const start = slotMinutes(a?.timeSlot);
    const end = endMinutes(a);
    return start >= 0 && end > start && nowMinutes >= start && nowMinutes < end;
  });
  if (inside !== -1) {
    const start = slotMinutes(rows[inside].timeSlot);
    const end = endMinutes(rows[inside]);
    return { index: inside, within: Math.round(((nowMinutes - start) / (end - start)) * 100) / 100 };
  }
  const next = rows.findIndex(a => !a?.isAllDay && slotMinutes(a?.timeSlot) > nowMinutes);
  return { index: next === -1 ? rows.length : next, within: null };
}

/**
 * Minutes behind (positive) or ahead (negative) of schedule, or null when nothing shows it yet.
 * The latest consultation to start says how late or early it began against its slot. A patient
 * who has arrived and is still waiting past their slot counts from when they could have been
 * seen (their slot, or their arrival if they came late), which shows the day slipping before
 * the next consultation starts.
 */
export function scheduleDrift(appointments: any[], now: Date): number | null {
  const day = moment(now).startOf('day');
  const scheduled = (a: any) => day.clone().add(slotMinutes(a.timeSlot), 'minutes');
  const counted = (appointments || []).filter(a =>
    a?.Patient && !a.isAllDay && a.timeSlot && a.isCancelled !== true && a.didNotAttend !== true);

  const started = counted.filter(a => a.startTime);
  const latest = started.length
    ? started.reduce((x, y) => (moment(y.startTime).isAfter(x.startTime) ? y : x))
    : null;
  const startDelay = latest ? moment(latest.startTime).diff(scheduled(latest), 'minutes') : null;

  const waits = counted
    .filter(a => a.patientArrivedTime && !a.startTime && !a.endTime)
    .map(a => moment(now).diff(moment.max(scheduled(a), moment(a.patientArrivedTime)), 'minutes'))
    .filter(m => m > 0);
  const longestWait = waits.length ? Math.max(...waits) : null;

  if (startDelay === null) return longestWait;
  return longestWait === null ? startDelay : Math.max(startDelay, longestWait);
}

/** Green up to 30 minutes behind (or any amount ahead), yellow to 60, red past that. */
export function driftLevel(minutes: number): DriftLevel {
  if (minutes > 60) return 'bad';
  if (minutes > 30) return 'warn';
  return 'good';
}

export function driftText(minutes: number): string {
  if (minutes === 0) return 'You seem to be on schedule';
  return `You seem to be ${duration(Math.abs(minutes))} ${minutes > 0 ? 'behind' : 'ahead of'} schedule`;
}

export function describeDrift(appointments: any[], now: Date): ScheduleDrift | null {
  const minutes = scheduleDrift(appointments, now);
  return minutes === null ? null : { minutes, level: driftLevel(minutes), text: driftText(minutes) };
}

function duration(minutes: number): string {
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** The stored end slot, else the end the calendar worked out from the type's length. */
function endMinutes(a: any): number {
  return slotMinutes(a?.endSlot ?? a?.appointmentEnd?.format?.('HH:mm'));
}

function slotMinutes(timeSlot: string | undefined): number {
  const [h, m] = (timeSlot || '').split(':').map(Number);
  return Number.isFinite(h) ? h * 60 + (Number.isFinite(m) ? m : 0) : -1;
}
